/**
 * Formats a payment amount from cents to a display string.
 * @param amount Amount in cents
 * @param currency Currency code (e.g., 'eur', 'usd')
 * @returns Formatted string like "12.50 EUR" or "-" if null
 */
export const formatAmount = (amount: number, currency: string): string => {
  if (amount == null) return '-';
  return `${(amount / 100).toFixed(2)} ${(currency || '').toUpperCase()}`;
};

/**
 * Sanitizes a string value for CSV export to prevent formula injection.
 * Prefixes values starting with =, +, -, @, tab, or carriage return with a single quote.
 */
export const sanitizeCsvValue = (val: string): string =>
  /^[=+\-@\t\r]/.test(val) ? `'${val}` : val;

/**
 * Maps a transaction status to a color scheme for badge display.
 */
export const getStatusColorScheme = (status: string, isSetupIntentPayment: boolean): string => {
  if (isSetupIntentPayment && status === 'pay_later') {
    return 'blue';
  }
  switch (status) {
    case 'succeeded':
      return 'green';
    case 'processing':
    case 'requires_action':
    case 'requires_confirmation':
    case 'requires_payment_method':
      return 'yellow';
    case 'canceled':
      return 'red';
    case 'refunded':
      return 'orange';
    case 'refunding':
      return 'yellow';
    default:
      return 'gray';
  }
};

/**
 * Maps a transaction type to a color scheme for badge display.
 */
export const getTypeColorScheme = (type: string): string => {
  switch (type) {
    case 'court_booking':
      return 'purple';
    case 'game_joining':
      return 'teal';
    case 'event':
      return 'cyan';
    case 'coaching':
      return 'orange';
    case 'credit_pack':
      return 'green';
    default:
      return 'gray';
  }
};

/**
 * Determines the payment method string for display/export.
 */
export const getPaymentMethodLabel = (
  paidWithWallet: boolean,
  isSetupIntentPayment: boolean,
  status: string,
  paymentMethodType?: string,
): 'wallet' | 'pay_later' | 'stripe' | 'manual_card' | 'manual_cash' => {
  if (paymentMethodType === 'manual_card') return 'manual_card';
  if (paymentMethodType === 'manual_cash') return 'manual_cash';
  if (paidWithWallet) return 'wallet';
  if (isSetupIntentPayment && status === 'pay_later') return 'pay_later';
  return 'stripe';
};
